import config from "@/utils/config";
import { decodeJwtPayload } from "@/utils/jwt";

export class PlatformAuthError extends Error {
  status?: number;
  // Machine-readable reason from the error body, e.g. 'MAGIC_LINK_THROTTLED'.
  code?: string;
  // Seconds the caller must wait before retrying, sent with a 429.
  retryAfterSeconds?: number;

  constructor(message: string, status?: number, payload?: any) {
    super(message);
    this.name = 'PlatformAuthError';
    this.status = status;

    if (typeof payload?.code === 'string') {
      this.code = payload.code;
    }

    const retryAfter = payload?.retryAfterSeconds ?? payload?.RetryAfterSeconds;
    if (typeof retryAfter === 'number') {
      this.retryAfterSeconds = retryAfter;
    }
  }
}

export function isSessionDenied(err: unknown): boolean {
  return err instanceof PlatformAuthError
    && (err.status === undefined || [400, 401, 403].includes(err.status));
}

let accessToken: string | null = null;
let refreshToken: string | null = null;

// Base URL for PlatformAuth API — injected by Aspire via VITE_AUTH_URL.
// Fall back to NEXT_PUBLIC_AUTH_URL during transitional environments/tests.
const AUTH_API_BASE = import.meta.env.VITE_AUTH_URL || import.meta.env.NEXT_PUBLIC_AUTH_URL || '';

/**
 * Whichever of the in-session token and the persisted copy expires later.
 *
 * The refresh heartbeat writes rotated tokens to storage only; `login()` and `switchTenant()` set `accessToken` only.
 * A soft-navigated tab therefore kept sending its login token here after storage had moved on, and got 401s once it expired.
 * During login the opposite holds: the freshly minted pair lives only in memory until it is persisted.
 * Comparing expiry keeps both cases on the newest token.
 */
const currentAccessToken = (): string | null => {
  const stored = storedAccessToken();
  if (!stored) return accessToken;
  if (!accessToken || accessToken === stored) return stored;
  return (decodeJwtExpiry(stored) ?? 0) >= (decodeJwtExpiry(accessToken) ?? 0) ? stored : accessToken;
};

const storedAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('sc:platformAccessToken');
  } catch {
    return null;
  }
};

const buildHeaders = (includeAuth = true) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'SC-Version': __APP_VERSION__
  };

  const token = includeAuth ? currentAccessToken() : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

/**
 * Chained with `||`: a 401 from PlatformAuth's fallback policy has an empty body and, over HTTP/2, an empty `statusText`.
 * With `??` that produced an empty message, which every modal then replaced with "Something went wrong".
 */
const extractErrorMessage = (payload: any, response: Response) => {
  return payload?.message || payload?.Message || response.statusText || statusFallbackMessage(response.status);
};

const statusFallbackMessage = (status: number) => {
  switch (status) {
    case 401: return SESSION_EXPIRED_MESSAGE;
    case 403: return 'You do not have permission to do that.';
    default: return 'Request failed';
  }
};

const parseJsonSafely = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

/** Must match the backend ConnectionType enum — it keys the AuthClient row used for session eviction. */
export enum ConnectionType {
  Web = 1,
  Mobile = 2,
}

export class PlatformAuthService {
  private apiUrl: string | null = null;
  private mobileApiUrl: string | null = null;
  private connectionType: ConnectionType;

  constructor(connectionType: ConnectionType = ConnectionType.Web) {
    this.connectionType = connectionType;
  }

  get accessTokenValue() {
    return accessToken;
  }

  set accessTokenValue(value: string | null) {
    accessToken = value;
  }

  get refreshTokenValue() {
    return refreshToken;
  }

  set refreshTokenValue(value: string | null) {
    refreshToken = value;
  }

    /** The platform user's GUID (the `sub` claim of the current access token), or null. */
        get userId(): string | null {
          return decodeJwtPayload(currentAccessToken())?.sub ?? null;
    }

  private setTokens(payload: any) {
    if (payload?.accessToken) {
      accessToken = payload.accessToken;
    }

    if (payload?.refreshToken) {
      refreshToken = payload.refreshToken;
    }

    this.apiUrl = payload?.apiUrl ?? null;
    this.mobileApiUrl = payload?.mobileApiUrl ?? null;
  }

  private clearTokens() {
    accessToken = null;
    refreshToken = null;
    this.apiUrl = null;
    this.mobileApiUrl = null;
  }

  private async handleResponse(response: Response) {
    if (!response.ok) {
      const payload = await parseJsonSafely(response);
      // The parsed body is handed to the error so throttled callers can read retryAfterSeconds.
      throw new PlatformAuthError(extractErrorMessage(payload, response), response.status, payload);
    }

    if (response.status === 204) {
      return {};
    }

    return await response.json();
  }

  /**
   * POSTs with the bearer token; on a 401 while a platform session exists it refreshes the pair and retries once.
   * Same rule as utils/Fetch.ts: a 401 means the token expired or the session was revoked, and only PlatformAuth can say which.
   * Without the retry every attempt after expiry fails the same way and the user cannot recover without reloading.
   * The refresh module is imported lazily because it depends on utils/storage, which pulls in the whole app.
   */
  private async postAuthorized(path: string, body: unknown): Promise<Response> {
    const send = () => fetch(`${AUTH_API_BASE}${path}`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(body)
    });

    const observedAccessToken = storedAccessToken();
    const response = await send();
    if (response.status !== 401 || !observedAccessToken) {
      return response;
    }

    const { refreshPlatformSession } = await import('@/services/platform-token-refresh');
    let refreshed: { accessToken: string } | null = null;
    try {
      refreshed = await refreshPlatformSession('unauthorized', observedAccessToken);
    } catch (err) {
      if (isSessionDenied(err)) {
        throw new PlatformAuthError(SESSION_EXPIRED_MESSAGE, 401);
      }
      throw err;
    }

    if (!refreshed) {
      // Logged out in another tab while we waited for the refresh lock.
      throw new PlatformAuthError(SESSION_EXPIRED_MESSAGE, 401);
    }

    // Keep the in-memory copy in step so a later call cannot resurrect the stale token.
    accessToken = refreshed.accessToken;
    return await send();
  }

  async login(email: string, password: string): Promise<any> {
    const response = await fetch(`${AUTH_API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: buildHeaders(false),
      body: JSON.stringify({ email, password, connectionType: this.connectionType })
    });

    const payload = await this.handleResponse(response);
    this.setTokens(payload);
    return payload;
  }

  async peekMagicLink(token: string): Promise<{ valid: boolean; expired: boolean; email?: string; companyName?: string; firstName?: string | null; lastName?: string | null }> {
    const response = await fetch(`${AUTH_API_BASE}/api/auth/magic-link/peek`, {
      method: 'POST',
      headers: buildHeaders(false),
      body: JSON.stringify({ token })
    });
    return await this.handleResponse(response);
  }

  // Mails and SMSes a fresh magic link for an expired or used invite token.
  // Throws a PlatformAuthError with status 429 and retryAfterSeconds when asked too often.
  async reissueMagicLink(token: string): Promise<{ sent: boolean }> {
    const response = await fetch(`${AUTH_API_BASE}/api/auth/magic-link/reissue`, {
      method: 'POST',
      headers: buildHeaders(false),
      body: JSON.stringify({ token })
    });
    return await this.handleResponse(response);
  }

  async consumeMagicLink(token: string): Promise<any> {
    const response = await fetch(`${AUTH_API_BASE}/api/auth/magic-link/consume`, {
      method: 'POST',
      headers: buildHeaders(false),
      body: JSON.stringify({ token, connectionType: this.connectionType })
    });

    const payload = await this.handleResponse(response);
    this.setTokens(payload);
    return payload;
  }

  async requestMagicLink(email: string): Promise<void> {
    const response = await fetch(`${AUTH_API_BASE}/api/auth/magic-link/request`, {
      method: 'POST',
      headers: buildHeaders(false),
      body: JSON.stringify({ email })
    });
    if (!response.ok) {
      const payload = await parseJsonSafely(response);
      throw new PlatformAuthError(extractErrorMessage(payload, response), response.status);
    }
  }

  async completeProfile(firstName: string, lastName: string, newPassword?: string): Promise<{ success: boolean }> {
    const response = await this.postAuthorized('/api/auth/complete-profile', {
      firstName,
      lastName,
      newPassword
    });
    return await this.handleResponse(response);
  }

  async refreshToken(): Promise<any> {
    if (!refreshToken) {
      throw new PlatformAuthError('Missing refresh token');
    }

    const response = await fetch(`${AUTH_API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: buildHeaders(false),
      body: JSON.stringify({ refreshToken })
    });

    const payload = await this.handleResponse(response);
    this.setTokens(payload);
    return payload;
  }

  async logout(): Promise<void> {
    const currentRefreshToken = refreshToken;
    this.clearTokens();

    if (!currentRefreshToken) return;

    // Fire-and-forget with keepalive so the request completes even during navigation.
    // 204 NoContent on success; controller always returns 204 to avoid token enumeration.
    fetch(`${AUTH_API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: buildHeaders(false),
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
      keepalive: true,
    }).catch(() => { /* best-effort — ignore network errors on logout */ });
  }

  async switchTenant(tenantId: string): Promise<any> {
    const response = await this.postAuthorized('/api/auth/switch-tenant', { tenantId });

    const payload = await this.handleResponse(response);
    this.setTokens(payload);
    return payload;
  }

    /**
     * Requests a password-reset email from PlatformAuth. Mirrors the login path: calls
     * PlatformAuth directly (NOT Manager). Returns void on success; PlatformAuth always
     * returns 200 to avoid user enumeration. The reset email link's base URL is provided by
     * PlatformAuth's configuration (PasswordReset:BaseUrl), so the client does not pass one.
     */
    async requestPasswordReset(email: string): Promise<void> {
      const response = await fetch(`${AUTH_API_BASE}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: buildHeaders(false),
        body: JSON.stringify({ email })
      });

      // 200 = ok (no enumeration); anything else is an error.
      if (!response.ok) {
        const payload = await parseJsonSafely(response);
        throw new PlatformAuthError(extractErrorMessage(payload, response), response.status);
      }
    }

    /**
     * Resets a password using a one-time reset token mailed by PlatformAuth. Mirrors the
     * login path: calls PlatformAuth directly (NOT Manager). On success returns
     * `{ success: true }` — a reset never mints a new session, so the user must re-authenticate
     * (the caller navigates to /login). A 400 body carries the reason (invalid/expired/used
     * token or a password-strength message).
     */
    async resetPassword(token: string, newPassword: string): Promise<{ success: boolean }> {
      const response = await fetch(`${AUTH_API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: buildHeaders(false),
        body: JSON.stringify({ token, newPassword })
      });

      if (!response.ok) {
        const payload = await parseJsonSafely(response);
        throw new PlatformAuthError(extractErrorMessage(payload, response), response.status);
      }

      return await this.handleResponse(response);
    }

          /**
           * Self-service password change for the logged-in user. Verifies the current password,
           * updates the new one, and keeps the current session alive. The subject is the `sub`
           * claim of the bearer token — the server ignores any body-supplied user id. Replaces
           * the legacy tenant-API `/Employee/ChangePassword` path.
           */
          async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
            const response = await this.postAuthorized('/api/auth/change-password', { currentPassword, newPassword });
            return await this.handleResponse(response);
          }

          /**
           * Admin/owner force-sets a user's password (no existing-password verification). Resolves
           * the target by email, revokes all target sessions. The server derives the actor from the
           * bearer token and requires Owner or UserManagement. Replaces the legacy tenant-API
           * `/Employee/OwnerChangePassword` path.
           */
          async setPassword(targetEmail: string, newPassword: string): Promise<{ success: boolean }> {
            const response = await this.postAuthorized('/api/auth/set-password', { targetEmail, newPassword });
            return await this.handleResponse(response);
          }
        }

export const JWT_PROACTIVE_REFRESH_SECONDS = 300; // Refresh 5 minutes before expiry

/**
 * Decodes the `exp` claim from a JWT payload (no signature verification — client-side only).
 * Returns the expiry as Unix epoch seconds, or null if the token cannot be decoded.
 */
export function decodeJwtExpiry(jwt: string): number | null {
  const json = decodeJwtPayload(jwt);
  return typeof json?.exp === 'number' ? json.exp : null;
}

/**
 * Unconditionally exchanges the refresh token for a new token pair, regardless of how
 * much lifetime the access token has left. This is the right call on a 401: a rejected
 * request with time left on the token means the session was revoked server-side
 * (permission change, device eviction), and only PlatformAuth can tell us whether the
 * session is still refreshable.
 *
 * Refresh tokens are single-use: PlatformAuth revokes the presented token and hands back a
 * replacement, and replaying a consumed one revokes the user's whole token family. App code
 * must therefore call `refreshPlatformSession` (services/platform-token-refresh) rather than
 * this function directly, so concurrent callers can never present the same token twice.
 *
 * @throws `PlatformAuthError` when the refresh is denied or fails. `status` carries the
 *         HTTP status when the server answered (401/403 ⇒ session revoked/denied);
 *         a missing refresh token throws without a status. Callers decide whether to log
 *         out with `isSessionDenied`; anything else is transient and should be retried.
 */
export async function refreshPlatformToken(
  currentRefreshToken: string | null,
): Promise<{ accessToken: string; refreshToken: string }> {
  if (!currentRefreshToken) {
    throw new PlatformAuthError('Missing refresh token');
  }

  const response = await fetch(`${AUTH_API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: buildHeaders(false),
    body: JSON.stringify({ refreshToken: currentRefreshToken }),
    signal: AbortSignal.timeout ? AbortSignal.timeout(20000) : undefined,
  });

  if (!response.ok) {
    const payload = await parseJsonSafely(response);
    throw new PlatformAuthError(extractErrorMessage(payload, response), response.status);
  }

  const payload = await response.json();
  return {
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
  };
}

/**
 * Proactively refreshes a PlatformAuth access token when it is expiring within
 * JWT_PROACTIVE_REFRESH_SECONDS (5 minutes).
 *
 * Unserialised, same as `refreshPlatformToken` — the heartbeat goes through
 * `refreshPlatformSession` (services/platform-token-refresh) instead.
 *
 * @returns New `{ accessToken, refreshToken }` if a refresh was performed; `null` if not needed.
 * @throws  `PlatformAuthError` if the refresh request fails; see `isSessionDenied`.
 */
export async function refreshPlatformTokenIfNeeded(
  currentAccessToken: string | null,
  currentRefreshToken: string | null,
): Promise<{ accessToken: string; refreshToken: string } | null> {
  if (!currentAccessToken || !currentRefreshToken) return null;

  const exp = decodeJwtExpiry(currentAccessToken);
  if (!exp) return null;

  const secondsUntilExpiry = exp - Math.floor(Date.now() / 1000);
  if (secondsUntilExpiry > JWT_PROACTIVE_REFRESH_SECONDS) return null;

  return refreshPlatformToken(currentRefreshToken);
}

/**
 * Calls the tenant API's hydration endpoint to retrieve employeeID, permissions,
 * fullName, pageSize, and subscription info after PlatformAuth login.
 * This targets the TENANT API (not PlatformAuth), using the PlatformAuth JWT as Bearer token.
 * Returns null on failure so callers can degrade gracefully.
 */
export async function hydrateTenantSession(tenantApiUrl: string, jwt: string): Promise<any | null> {
  try {
    // Normalise: callers may pass a URL that already includes /api (from the api_url JWT claim
    // or config.apiHost). Strip a trailing /api so we never produce a double /api/api path.

    // cookie not set yet so we need to duplicate the API pinned logic
    let baseUrl = config.pinnedApiHost ? config.pinnedApiHost : tenantApiUrl;
    baseUrl = baseUrl.replace(/\/api\/?$/, '');
    const response = await fetch(`${baseUrl}/api/auth/hydrate`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Accept': 'application/json',
        'SC-Version': __APP_VERSION__
      },
      signal: AbortSignal.timeout(8000)
    });

    if (!response.ok) {
      console.warn(`[AUTH] Hydration returned ${response.status}: ${response.statusText}`);
      return null;
    }

    return await response.json();
  } catch (err) {
    console.warn('[AUTH] Hydration request failed:', err);
    return null;
  }
}
